// CategoryFormModal.tsx — create or edit a proposal category (WordPress-style:
// a name, an optional slug and an optional description). Admin-only: it is only
// opened from the admin Proposals page, and the API refuses anyone else.

import React, { useEffect, useState } from 'react';
import { Check, FolderPlus, Pencil, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import ProposalDialog, { Notice } from './ProposalDialog';
import { ProposalCategory, apiError, proposalApi } from './proposalTypes';

const NAME_MAX = 80;
const DESC_MAX = 300;

interface CategoryFormModalProps {
  visible: boolean;
  /** null → create, a category → edit */
  category: ProposalCategory | null;
  onHide: () => void;
  onSaved: (c: ProposalCategory) => void;
}

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({ visible, category, onHide, onSaved }) => {
  const isEdit = !!category;
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setName(category?.name ?? '');
    setSlug(category?.slug ?? '');
    setDescription(category?.description ?? '');
    setError(null);
  }, [visible, category]);

  const submit = async () => {
    const n = name.trim();
    const s = slug.trim();
    const d = description.trim();
    if (!n) return setError('Give the category a name.');
    if (n.length > NAME_MAX) return setError(`The name can be at most ${NAME_MAX} characters.`);
    if (d.length > DESC_MAX) return setError(`The description can be at most ${DESC_MAX} characters.`);

    setSaving(true);
    setError(null);
    try {
      if (isEdit && category) {
        // send only what changed
        const body: { name?: string; slug?: string; description?: string } = {};
        if (n !== category.name) body.name = n;
        if (s && s !== category.slug) body.slug = s;
        if (d !== (category.description ?? '')) body.description = d; // "" clears it
        if (Object.keys(body).length === 0) {
          onHide();
          return;
        }
        const res = await proposalApi.categories.update(category.id, body);
        onSaved(res.data);
      } else {
        const res = await proposalApi.categories.create({ name: n, slug: s || undefined, description: d || undefined });
        onSaved(res.data);
      }
      onHide();
    } catch (e) {
      setError(apiError(e, isEdit ? 'Could not update the category.' : 'Could not create the category.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProposalDialog
      visible={visible}
      onHide={onHide}
      title={isEdit ? 'Edit category' : 'New category'}
      subtitle={isEdit ? 'Rename it or change the description' : 'Group templates the way you think about them'}
      icon={isEdit ? Pencil : FolderPlus}
      busy={saving}
      maxWidth="460px"
      footer={
        <>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={onHide} disabled={saving} />
          <GlobalButton label={isEdit ? 'SAVE CHANGES' : 'CREATE CATEGORY'} icon={Check} onClick={submit} loading={saving} />
        </>
      }
    >
      {error && <Notice tone="error">{error}</Notice>}

      <GlobalInputField
        label="Name"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Course offers"
        maxLength={NAME_MAX}
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
        inputProps={{ autoFocus: true }}
      />

      <GlobalInputField
        label="Slug"
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        placeholder="course-offers"
        hint={isEdit ? 'The short name used in links. Changing it is rarely needed.' : 'Leave blank to make it from the name.'}
        disabled={saving}
      />

      <GlobalTextArea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What kind of templates go here? (optional)"
        rows={3}
        maxLength={DESC_MAX}
        disabled={saving}
      />
    </ProposalDialog>
  );
};

export default CategoryFormModal;
